import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Users, Heart, Armchair, QrCode, Zap, CheckCircle } from 'lucide-react';

function SolutionSection({ content }) {
  const pillars = [
    {
      icon: Briefcase,
      title: 'Job Connect',
      description: 'Employers post openings, talent receives instant notifications. Full-time, part-time, and contract opportunities matched in real-time.',
      priority: 'PRIMARY',
      color: 'kore',
      features: ['Instant job alerts', 'One-tap apply', 'Smart matching']
    },
    {
      icon: Users,
      title: 'Professional Network',
      description: 'Build your career identity with portfolios, credentials, and professional connections. Follow peers, mentors, and industry leaders.',
      priority: 'PRIMARY',
      color: 'lavender',
      features: ['Rich portfolios', 'Credential verification', 'Industry connections']
    },
    {
      icon: Heart,
      title: 'Social Engagement',
      description: 'Instagram/TikTok-style content creation keeps users engaged daily. Share work, learn techniques, build community.',
      priority: 'PRIMARY',
      color: 'coral',
      features: ['Content creation', 'Trending feed', 'Community building']
    },
    {
      icon: Armchair,
      title: 'Chair-Swapping',
      description: 'Unique marketplace for businesses to rent unused chairs to independent professionals. Zero competition. Strong moat.',
      priority: 'SECONDARY',
      color: 'mint',
      features: ['Workspace marketplace', 'Recurring rentals', 'Zero competition']
    }
  ];

  const colorClasses = {
    kore: 'from-kore-500 to-kore-700',
    lavender: 'from-accent-lavender to-purple-700',
    coral: 'from-accent-coral to-red-700',
    mint: 'from-accent-mint to-green-700'
  };

  const bgClasses = {
    kore: 'bg-kore-500/10 border-kore-500/20',
    lavender: 'bg-accent-lavender/10 border-accent-lavender/20',
    coral: 'bg-accent-coral/10 border-accent-coral/20',
    mint: 'bg-accent-mint/10 border-accent-mint/20'
  };

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-kore-500/10 border border-kore-500/20 rounded-full mb-6">
            <Zap className="w-4 h-4 text-kore-400" />
            <span className="text-sm text-kore-400">Our Solution</span>
          </div>
          <h2 className="section-title mb-4">
            The <span className="text-gradient">LinkedIn + Instagram</span>
            <br />of Beauty
          </h2>
          <p className="section-subtitle mx-auto">
            One platform that combines professional networking with social engagement, built specifically for the beauty industry.
          </p>
        </motion.div>

        {/* Pillars Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className={`card hover-lift ${pillar.priority === 'PRIMARY' ? 'md:col-span-1' : ''}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[pillar.color]}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${bgClasses[pillar.color]} border`}>
                    {pillar.priority}
                  </span>
                </div>

                <h3 className="text-xl font-semibold mb-2">{pillar.title}</h3>
                <p className="text-zinc-400 text-sm mb-4">{pillar.description}</p>

                <div className="flex flex-wrap gap-2">
                  {pillar.features.map((feature) => (
                    <div 
                      key={feature}
                      className="flex items-center gap-1 text-xs text-zinc-500"
                    >
                      <CheckCircle className="w-3 h-3 text-accent-mint" />
                      {feature}
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* KoreCode Feature */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card bg-gradient-to-r from-kore-900/50 to-accent-lavender/20 border-kore-500/30"
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0 p-4 rounded-2xl bg-white/10">
              <QrCode className="w-12 h-12 text-kore-400" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-semibold mb-2">
                <span className="text-gradient">KoreCode</span> — Viral Profile Sharing
              </h3>
              <p className="text-zinc-400 text-sm">
                Every professional gets a unique QR code and custom URL (korefolio.com/@name) for seamless, viral sharing. 
                Scan to connect, follow, or book instantly.
              </p>
            </div>
            <div className="flex-shrink-0 text-center">
              <div className="text-2xl font-bold text-accent-mint">$0 CAC</div>
              <div className="text-xs text-zinc-500">Organic growth</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default SolutionSection;
